<template lang="pug">
dv-server-form(
  ref="form"
  v-model="form"
  :schema="schema"
  :method="method"
  :success-callback="onSuccess"
  :error-callback="onError"
  :nestable="false"
  v-slot="{ fields, loading, invalid }"
)
  .d-flex.flex-column.gap-16
    dv-alert(v-if="errorMessage" color="ui-error" role="alert") {{ errorMessage }}
    dv-form-field(:field="fields.email" :disabled="awaitingCode || loading")
    template(v-if="awaitingCode")
      p.ui-body-2 На новую почту отправлен код подтверждения.
      v-sheet.align-self-center(max-width="100%" width="360")
        dv-form-field(:field="fields.code" :disabled="loading")
    .d-flex.flex-wrap.align-center.gap-16
      dv-btn(large :loading="loading" :disabled="invalid || loading || !hasChanges" type="submit") {{ awaitingCode ? 'Подтвердить почту' : 'Сохранить email' }}
      dv-btn(large text :disabled="loading" @click="$emit('cancel')") Отмена
</template>

<script>
import DvAlert from '@/packages/ui/components/alerts/DvAlert.vue';
import DvBtn from '@/packages/ui/components/buttons/DvBtn.vue';
import DvFormField from '@/packages/ui/components/forms/DvFormField.vue';
import DvServerForm from '@/packages/ui/components/forms/DvServerForm.vue';
import DvTextField from '@/packages/ui/components/inputs/DvTextField.vue';
import getApiErrorMessage from '@/helpers/getApiErrorMessage';
import { assertActive, SignatureClientError } from '../../services/cryptoPro';
// TEMP_SIGNATURE_PREVIEW
import { isSignaturePreviewContext } from '../../services/signaturePreview';

const EMAIL_CODE_LENGTH = 6;

export default {
  components: { DvAlert, DvBtn, DvFormField, DvServerForm },
  props: {
    context: { type: Object, required: true },
    initial: { type: String, default: '' },
    pending: { type: Object, default: null },
    requests: { type: Array, default: () => [] },
    isActive: { type: Function, required: true },
  },
  data() {
    return { form: { email: this.pending?.new || this.initial, code: '' }, initialEmail: this.initial, awaitingCode: !!this.pending, errorMessage: '', refreshFailed: false, submitting: false, disposed: false };
  },
  computed: {
    hasChanges() {
      return this.awaitingCode ? !!this.form.code : !this.requests.length && this.form.email.trim() !== this.initialEmail;
    },
    schema() {
      return [
        {
          name: 'email',
          value: 'email',
          label: 'Email',
          rules: 'required|email|max:255',
          component: DvTextField,
          props: { autocomplete: 'email' },
        },
        ...this.awaitingCode ? [
          {
            name: 'code',
            value: 'code',
            label: 'Код подтверждения',
            rules: `required|numeric|length:${EMAIL_CODE_LENGTH}`,
            component: 'v-otp-input',
            props: { length: EMAIL_CODE_LENGTH, type: 'number', autocomplete: 'one-time-code' },
          },
        ] : [],
      ];
    },
  },
  beforeDestroy() {
    this.disposed = true;
    this.form = { email: '', code: '' };
  },
  methods: {
    active() { return !this.disposed && this.isActive(); },
    async method() {
      if (this.submitting || !this.active() || !this.hasChanges) return { error: new SignatureClientError('Операция отменена.') };
      this.submitting = true;
      this.errorMessage = '';
      this.refreshFailed = false;
      try {
        const data = { email: this.form.email.trim(), ...this.awaitingCode ? { code: this.form.code } : {} };
        const { obs } = this.$refs.form.$refs.form.$refs;
        const params = { data, obs, organizationId: this.context.organizationId, isActive: this.active };
        let result;
        if (this.awaitingCode) result = await this.$models.auth.accountSetup.confirmEmailChange(params);
        else if (!this.initialEmail) result = await this.$models.auth.accountSetup.setOrganizationEmail({ ...params, args: [this.context.organizationId] });
        else result = await this.$models.auth.accountSetup.sendEmailChange(params);
        assertActive(this.active);
        if (result.error) {
          // A different client may have created a blocking request after the editor opened.
          // TEMP_SIGNATURE_PREVIEW: do not reload real user state in preview.
          if (!this.awaitingCode && !isSignaturePreviewContext(this.context)) {
            try {
              await this.$user.reloadUser();
              if (this.active()) this.$emit('refresh');
            } catch (error) { /* Keep the original request error and draft. */ }
          }
          return result;
        }
        // TEMP_SIGNATURE_PREVIEW: never reload the real user for a dummy account.
        if ((this.awaitingCode || !this.initialEmail) && !isSignaturePreviewContext(this.context)) {
          try {
            await this.$user.reloadUser();
            const refreshed = this.awaitingCode
              ? this.$user.data.change_emails?.some((item) => item.new === data.email && item.change_at)
              : this.$user.data.organizer?.contact_email === data.email;
            this.refreshFailed = !refreshed;
          } catch (error) {
            this.refreshFailed = true;
          }
          assertActive(this.active);
        }
        return result;
      } catch (error) {
        return { error };
      } finally {
        this.submitting = false;
      }
    },
    onSuccess({ data }) {
      if (!this.active()) return;
      if (this.initialEmail && !this.awaitingCode) {
        this.awaitingCode = true;
        this.$toast.success('Код отправлен на новую почту');
        this.$emit('request', data);
        return;
      }
      // Keep the successful mutation's result even if refreshing the account fails.
      if (this.refreshFailed) this.$toast.error('Email сохранён, но не удалось обновить аккаунт. Обновите страницу.');
      else this.$toast.success(this.awaitingCode ? 'Смена почты подтверждена' : 'Email сохранён');
      this.$emit('success', data);
    },
    onError({ data }) {
      if (this.active()) this.errorMessage = data?.signatureClientError ? data.message : getApiErrorMessage(data);
    },
  },
};
</script>
